import { useState } from 'react';
import { Card, Container, Button, Input, Stack, Icon, Textarea, Text } from '@chakra-ui/react';
import slugify from 'slugify';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { InputGroup } from '@/components/ui/input-group';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { Field } from '@/components/ui/field';
import { Radio, RadioGroup } from '@/components/ui/radio';
import { Checkbox } from '@/components/ui/checkbox';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import api from '@/utils/api';
import { BlockType, SelectionType, DateType } from '@/lib/types';
import { FIELD_TYPES } from '@/lib/constants';

interface Option {
  is_default: boolean;
  option_label: string;
  option_value: string;
}

const formSchema = z.object({
  name: z.string().min(1, 'The name is required'),
  unique_name: z.string().optional(),
  field_type: z.string().min(1, 'The field type is required'),
  options: z.string().optional(),
  properties: z.object({
    selectionType: z.string().optional(),
    minlength: z.number().nullable().optional(),
    maxlength: z.number().nullable().optional(),
    dateType: z.string().optional(),
    showInCalendarView: z.boolean().optional(),
    placeholder: z.string().optional(),
  }),
});

type FormValues = z.infer<typeof formSchema>;

export default function CreateField() {
  const { workspaceId, fieldType } = useParams<{
    workspaceId: string;
    fieldType: string;
  }>();
  const [optionErrors, setOptionErrors] = useState<string[]>([]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
          defaultValues: {
        name: '',
        unique_name: '',
        options: '',
        properties: {
          selectionType: SelectionType.Single,
          minlength: null,
          maxlength: null,
          placeholder: '',
          dateType: DateType.Date,
          showInCalendarView: false,
        },
      },
  });

  const validateOptions = (optionsText: string): string[] => {
    const errors: string[] = [];
    if (!optionsText.trim()) return errors;

    const optionValues = new Set<string>();

    optionsText.split('\n').forEach((line, index) => {
      if (!line.trim()) return;

      const parts = line.split(',');
      if (parts.length !== 2) {
        errors.push(
          `Line ${index + 1}: Each line must contain a label and value separated by a comma`,
        );
        return;
      }

      const [optionLabel, rawValue] = parts;
      if (!optionLabel.trim()) {
        errors.push(`Line ${index + 1}: Option label cannot be empty`);
      }

      const optionValue = rawValue.replace(/\s+/g, '');
      if (!optionValue) {
        errors.push(`Line ${index + 1}: Option value cannot be empty`);
        return;
      }

      // Check for duplicates
      if (optionValues.has(optionValue)) {
        errors.push(`Line ${index + 1}: Option value "${optionValue}" is a duplicate`);
        return;
      }

      optionValues.add(optionValue);
    });

    return errors;
  };

  const onSubmit = async (data: FormValues) => {
    if (!workspaceId || !fieldType) {
      return;
    }

    if (parseInt(fieldType) === BlockType.Selection && data.options) {
      const errors = validateOptions(data.options);
      if (errors.length > 0) {
        setOptionErrors(errors);
        // Scroll to the options field
        document
          .getElementById('options-field')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    try {
      const fieldOptions: Option[] = [];
      if (data.options) {
        const optionItems = data.options
          .split('\n')
          .filter((item) => item.trim())
          .map((item) => {
            const [optionLabel, rawValue] = item.split(',');
            return {
              is_default: false,
              option_label: optionLabel.trim(),
              option_value: rawValue.replace(/\s+/g, ''),
            };
          });
        fieldOptions.push(...optionItems);
      }

      const properties = { ...data.properties };
      switch (parseInt(fieldType)) {
        case BlockType.Text:
          delete properties.selectionType;
          delete properties.dateType;
          delete properties.showInCalendarView;
          break;
        case BlockType.Date:
          delete properties.selectionType;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          break;
        case BlockType.Selection:
          delete properties.dateType;
          delete properties.showInCalendarView;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          break;
        case BlockType.LongText:
          delete properties.dateType;
          delete properties.showInCalendarView;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          break;
        default:
          delete properties.selectionType;
          delete properties.dateType;
          delete properties.showInCalendarView;
          delete properties.minlength;
          delete properties.maxlength;
          delete properties.placeholder;
          break;
      }

      await api.post(`fields`, {
        name: data.name,
        unique_name: data.unique_name,
        field_type: parseInt(fieldType),
        workspace_id: parseInt(workspaceId),
        properties: properties,
        field_options: fieldOptions,
      });
      window.location.href = `/${workspaceId}/fields`;
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create field',
      });
    }
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'3xl'}>
            <Card.Title fontSize='3xl' mb={4}>
              Create Field
            </Card.Title>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form id={'createField'} onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={4} mb={8}>
                <Field
                  label='Field Type'
                  invalid={!!errors.field_type}
                  errorText={errors.field_type?.message}
                  disabled
                >
                  <NativeSelectRoot disabled>
                    <NativeSelectField {...register('field_type')}>
                      {FIELD_TYPES.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
                <Field label='Field Name' invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input
                    {...register('name')}
                    onChange={(e) => {
                      const uniqueName = slugify(e.target.value, {
                        lower: true,
                        strict: true,
                      });
                      setValue('unique_name', uniqueName);
                    }}
                  />
                </Field>
                <Field label='Unique Name' disabled>
                  <InputGroup
                    flex='1'
                    w={'full'}
                    endElement={<Icon as={LockClosedIcon} w={4} h={4} />}
                  >
                    <Input {...register('unique_name')} disabled />
                  </InputGroup>
                </Field>
                
                {fieldType && parseInt(fieldType, 10) === BlockType.Text && (
                  <>
                    <Field
                      label='Placeholder'
                      invalid={!!errors.properties?.placeholder}
                      errorText={errors.properties?.placeholder?.message}
                      helperText='Placeholder is optional. It is a short hint that describes the expected value of the field.'
                    >
                      <Input {...register('properties.placeholder')} />
                    </Field>
                    <Field
                      label='Minimum Characters'
                      invalid={!!errors.properties?.minlength}
                      errorText={errors.properties?.minlength?.message}
                      helperText='Minimum Characters is optional. It must be a whole number less than or equal to Maximum Characters (if set).'
                    >
                      <Input
                        {...register('properties.minlength', {
                          setValueAs: (value) => (!value ? null : parseInt(value, 10)),
                        })}
                        type='number'
                        placeholder={'e.g. 10, 140, etc'}
                      />
                    </Field>
                    <Field
                      label='Maximum Characters'
                      invalid={!!errors.properties?.maxlength}
                      errorText={errors.properties?.maxlength?.message}
                      helperText='Maximum Characters is optional. It must be a whole number greater than or equal to Minimum Characters (if set).'
                    >
                      <Input
                        {...register('properties.maxlength', {
                          setValueAs: (value) => (!value ? null : parseInt(value, 10)),
                        })}
                        type='number'
                        placeholder={'e.g. 10, 140, etc'}
                      />
                    </Field>
                  </>
                )}
                {fieldType && parseInt(fieldType, 10) === BlockType.Date && (
                  <>
                    <Controller
                      control={control}
                      name='properties.dateType'
                      render={({ field }) => (
                        <Field label='Type'>
                          <RadioGroup
                            name={field.name}
                            value={field.value}
                            onValueChange={({ value }) => {
                              field.onChange(value);
                            }}
                            pl={4}
                          >
                            <Stack gap={2}>
                              <Radio value={DateType.Date}>Date</Radio>
                              <Radio value={DateType.Datetime}>Datetime</Radio>
                            </Stack>
                          </RadioGroup>
                        </Field>
                      )}
                    />

                    {watch('properties.dateType') === DateType.Datetime && (
                      <Controller
                        control={control}
                        name='properties.showInCalendarView'
                        render={({ field }) => (
                          <Field>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={({ checked }) => field.onChange(checked)}
                            >
                              Show in Calendar View
                            </Checkbox>
                          </Field>
                        )}
                      />
                    )}
                  </>
                )}
                {fieldType && parseInt(fieldType, 10) === BlockType.Selection && (
                  <>
                    <Controller
                      control={control}
                      name='properties.selectionType'
                      render={({ field }) => (
                        <Field label='Selection Type'>
                          <RadioGroup
                            name={field.name}
                            value={field.value}
                            onValueChange={({ value }) => {
                              field.onChange(value);
                            }}
                            pl={4}
                          >
                            <Stack gap={2}>
                              <Radio value={SelectionType.Single}>Single</Radio>
                              <Radio value={SelectionType.Multi}>Multi</Radio>
                            </Stack>
                          </RadioGroup>
                        </Field>
                      )}
                    />
                    <Field
                      id='options-field'
                      label='Field Options'
                      invalid={optionErrors.length > 0}
                      errorText={
                        optionErrors.length > 0 ? 'Please fix the errors below' : undefined
                      }
                      helperText='Enter each option on a new line with format: Label,value'
                    >
                      <Textarea
                        {...register('options')}
                        placeholder={'First Option,option1\nSecond Option,option2'}
                        rows={8}
                        onChange={(e) => {
                          if (optionErrors.length > 0) setOptionErrors([]);

                          setValue('options', e.target.value);
                        }}
                      />

                      {optionErrors.length > 0 && (
                        <Stack mt={2} gap={1}>
                          {optionErrors.map((error, idx) => (
                            <Text key={idx} color='red.500' fontSize='sm'>
                              {error}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </Field>
                  </>
                )}
              </Stack>
              <Button
                colorPalette='brand'
                form={'createField'}
                type={'submit'}
                width={'full'}
                loading={isSubmitting}
                loadingText={'Loading...'}
              >
                Save
              </Button>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
